<template>
  <div class="edit-media-container mb-3">
      <!-- Header Section -->
      <div class="edit-header d-flex justify-content-between align-items-center mb-4 mt-2">
        <div class="d-flex align-items-center">
            <h4 class="mb-0 mr-3 text-light font-weight-bold">{{ currentMeta.title || mediaEdit.title || 'Untitled' }}</h4>
            <b-badge variant="dark" class="mr-2 text-monospace opacity-50">{{ mediaEdit.id }}</b-badge>
        </div>
        <div class="d-flex align-items-center edit-actions">
           <b-form-select id="language-select" v-model="selectedLanguage" :options="$store.state.media.options.language" class="language-select mr-3" size="sm"/>
           <b-button v-if="selectedLanguage != mediaEdit.default_language" @click="deleteMeta" v-b-tooltip.hover title="Delete language meta" variant="danger" class="mr-3" size="sm">
              <font-awesome-icon icon="trash-alt"/>
           </b-button>

           <b-button class="mx-1" variant="danger" @click.stop="$emit('close', mediaId)" v-b-tooltip.hover title="Close">
             <font-awesome-icon icon="times"/>
           </b-button>
           <b-button class="mx-1" variant="primary" @click="$emit('save', {id: mediaId})" v-b-tooltip.hover title="Save">
             <Icon icon="Save"/>
           </b-button>
        </div>
      </div>

      <b-form class="edit-form-grid">
        <!-- LEFT COLUMN (Main Content) -->
        <div class="grid-main">
            <!-- PANEL: Essentials -->
            <div class="edit-panel">
                <h5 class="panel-title">Essentials</h5>
                <b-form-group label="Title" label-for="title-input">
                  <b-form-input id="title-input" v-model="metaTitle" :state="titleState" placeholder="Enter title..."></b-form-input>
                </b-form-group>
                
                <b-row>
                    <b-col md="6">
                         <b-form-group label="Slug" label-for="slug-input">
                            <b-input-group>
                                <b-form-input id="slug-input" v-model="metaSlug" :state="slugState"></b-form-input>
                                <b-input-group-append>
                                    <b-button @click.stop="makeSlugFromTitle" variant="outline-light">Auto</b-button>
                                </b-input-group-append>
                            </b-input-group>
                         </b-form-group>
                    </b-col>
                    <b-col md="6">
                         <b-form-group label="Secondary Title" label-for="sec-title-input">
                            <b-input-group>
                                <b-form-input id="sec-title-input" v-model="metaSecTitle"></b-form-input>
                                <b-input-group-append>
                                    <b-button v-if="$config.enableArtistsEverywhere" @click.stop="makeSecTitleFromArtists" variant="outline-light">From Artists</b-button>
                                </b-input-group-append>
                            </b-input-group>
                         </b-form-group>
                    </b-col>
                </b-row>

               <template v-if="mediaEdit.show_meta_text && $helpers.hasFullMeta(media.type)">
                  <b-form-group label="Short Description" label-for="short-desc-textarea">
                    <b-form-textarea id="short-desc-textarea" v-model="metaShortDescription" rows="3"></b-form-textarea>
                  </b-form-group>
                  <b-form-group label="Long Description" label-for="long-desc-textarea">
                    <b-form-textarea id="long-desc-textarea" v-model="metaLongDescription" style="height: 200px;"></b-form-textarea>
                  </b-form-group>
                   <b-form-group label="Credits" label-for="credits-edit">
                    <Credits id="credits-edit" v-model="itemCredits"></Credits>
                  </b-form-group>
                </template>

                 <template v-if="$helpers.hasCallToAction(media.type)">
                     <!-- Refactored CTA Toggle -->
                     <b-form-group class="mb-3">
                         <div class="d-flex align-items-center mb-2">
                             <h6 class="mb-0 mr-3 font-weight-bold" style="letter-spacing: 0.5px; font-size: 0.9rem; color: #fff;">Call To Action</h6>
                             <b-form-checkbox id="c2a-enable-checkbox" v-model="mediaEdit.call_to_action_enabled" switch size="lg"></b-form-checkbox>
                         </div>
                       <div v-if="mediaEdit.call_to_action_enabled" class="pl-3 border-left border-primary">
                         <SimpleAutoComplete v-model="metaCallToAction" :items="c2aItems" placeholder="Button Text"></SimpleAutoComplete>
                         <b-form-input id="c2a-link-input" class="mt-2" v-model="metaCallToActionURL" :state="c2aUrlState" placeholder="URL (starting with https://)"></b-form-input>
                       </div>
                     </b-form-group>
                </template>
                <template v-else-if="media.type == 'external'">
                  <b-form-group label="Link to" label-for="link-input">
                    <b-form-input id="link-input" v-model="metaLinkTo" placeholder="URL (starting with https://)"></b-form-input>
                  </b-form-group>
                </template>
            </div>

            <!-- PANEL: Visuals -->
            <div class="edit-panel" v-if="hasCovers || hasSlides || hasThumbnails">
                <h5 class="panel-title">Visuals</h5>
                <b-form-group v-if="hasCovers" label="Covers" label-for="covers-list">
                  <ImageList id="covers-list" :item-id="mediaEdit.id" :item-type="mediaEdit.type" :language="selectedLanguage" v-model="metaCovers" type="cover" :allow-copy-if-empty="Object.values($store.state.app.languages).length > 1" @copy-from="copyCoversFrom" @reload="tryReload(4000)"></ImageList>
                </b-form-group>

                <b-form-group v-if="hasSlides" label="Slides" label-for="slides-list">
                  <ImageList id="slides-list" :item-id="mediaEdit.id" :item-type="mediaEdit.type" :language="selectedLanguage" v-model="metaSlides" type="slide" :allow-copy-if-empty="Object.values($store.state.app.languages).length > 1" :allow-link-to="!mediaEdit.call_to_action_enabled" @copy-from="copyCoversFrom" @reload="tryReload(4000)"></ImageList>
                </b-form-group>
                
                 <b-form-group v-if="hasThumbnails" label="Thumbnails" label-for="thumbnails-list">
                  <ImageList id="thumbnails-list" :item-id="mediaEdit.id" :item-type="mediaEdit.type" :language="selectedLanguage" v-model="mediaEdit.thumbnails" type="thumbnail"  @reload="tryReload(4000)"></ImageList>
                </b-form-group>
                
                 <b-form-group v-if="mediaEdit.preview_url || mediaEdit.type == 'category' || mediaEdit.type == 'page' || mediaEdit.type == 'artist_page' || mediaEdit.type == 'playlist_page'" label="Preview URL" label-for="preview-url-input">
                  <b-input-group>
                    <b-input id="preview-url-input" :value="mediaEdit.preview_url" type="text" readonly></b-input>
                    <b-input-group-append>
                      <b-button v-if="mediaEdit.type == 'category' || mediaEdit.type == 'page' || mediaEdit.type == 'artist_page' || mediaEdit.type == 'playlist_page'" variant="primary" @click="selectPreviewUrl">
                        <font-awesome-icon icon="search"/>
                      </b-button>
                      <b-button variant="danger" @click="mediaEdit.preview_url = null">
                        <font-awesome-icon icon="trash-alt"/>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                 </b-form-group>
            </div>

            <!-- PANEL: Media & Assets -->
            <div class="edit-panel" v-if="$helpers.hasAssets(mediaEdit.type) || media.type == 'video' || media.type == 'episode'">
                 <h5 class="panel-title">Media & Assets</h5>
                 
                 <template v-if="$helpers.hasAssets(mediaEdit.type)">
                  <b-form-group v-if="$helpers.isExternalAsset(mediaEdit.asset_format)" label="Assets" label-for="asset-list">
                    <EmbeddedAsset id="asset-list" :media-item-id="mediaEdit.id" :assets="mediaEdit.assets" @delete-asset="deleteAsset($event)"></EmbeddedAsset>
                  </b-form-group>
                  <b-form-group v-else-if="$helpers.isInternalAsset(mediaEdit.asset_format) && mediaEdit.assets" label="Assets" label-for="asset-list">
                    <AssetList id="asset-list" :items="mediaEdit.assets" :item-id="mediaEdit.id" :format="mediaEdit.asset_format" :language="selectedLanguage" @delete-asset="deleteAsset($event)" @retranscode-asset="retranscodeAsset($event)" @update="tryReload(4000)"></AssetList>
                  </b-form-group>
                </template>
                
                 <b-form-group v-if="media.type == 'video' || media.type == 'episode'" label="Subtitles" label-for="subtitle-list">
                  <SubtitleList id="subtitle-list" :subtitles="mediaEdit.subtitles" :item-id="mediaEdit.id" @reload="tryReload(4000)"></SubtitleList>
                </b-form-group>
                
                 <template v-if="$helpers.isLivestreamable(media.type)">
                    <!-- Refactored Livestream Toggle -->
                    <b-form-group class="mb-0">
                        <div class="d-flex align-items-center mb-2">
                             <h6 class="mb-0 mr-3 font-weight-bold" style="letter-spacing: 0.5px; font-size: 0.9rem; color: #fff;">Livestream</h6>
                             <b-form-checkbox id="is-livestream-checkbox" v-model="itemIsLivestream" switch size="lg"></b-form-checkbox>
                        </div>
                      <div v-if="itemIsLivestream" class="mt-2 pl-3 border-left border-primary">
                        <b-form-group label="Timezone" label-for="livestream-timezone-select" description="Timezone the start/end times are intended for.">
                          <b-form-select id="livestream-timezone-select" v-model="itemLivestreamTimezone" :options="timezoneOptions"/>
                        </b-form-group>

                        <b-form-group label="Schedule (start / end)" label-for="datetime-livestream-comp">
                          <Publication id="datetime-livestream-comp" v-model="mediaEdit" :zone="itemLivestreamTimezone" from-field="datetime_livestream_start" to-field="datetime_livestream_end"/>
                        </b-form-group>

                        <b-form-group class="mb-2">
                          <b-form-checkbox id="livestream-finished-checkbox" v-model="itemLivestreamFinished" switch>
                            <span class="ml-2">End livestream now (mark finished)</span>
                          </b-form-checkbox>
                        </b-form-group>

                        <b-form-group label="Finished URL (optional VOD after the stream ends)" label-for="livestream-finished-url-input">
                          <b-input-group>
                            <b-form-select id="livestream-finished-url-type" v-model="itemLivestreamFinishedUrlType" :options="finishedUrlTypeOptions" style="max-width: 9rem;"/>
                            <b-form-input id="livestream-finished-url-input" v-model="itemLivestreamFinishedUrl" :state="finishedUrlState" placeholder="https://.../playlist.m3u8"/>
                          </b-input-group>
                          <b-form-invalid-feedback :state="finishedUrlState">
                            {{ itemLivestreamFinishedUrlType == 'm3u8' ? 'URL must start with http and end with .m3u8' : 'URL must start with http' }}
                          </b-form-invalid-feedback>
                        </b-form-group>

                        <b-form-group label="Recording start offset" description="Skip this much padding at the start of the recording.">
                          <b-input-group>
                            <b-form-input v-model.number="itemLivestreamFinishedStartOffsetMinutes" type="number" min="0" placeholder="0" aria-label="Minutes"/>
                            <b-input-group-append is-text>min</b-input-group-append>
                            <b-form-input v-model.number="itemLivestreamFinishedStartOffsetSeconds" type="number" min="0" max="59" placeholder="0" aria-label="Seconds"/>
                            <b-input-group-append is-text>sec</b-input-group-append>
                          </b-input-group>
                        </b-form-group>
                      </div>
                    </b-form-group>
                  </template>
            </div>

             <!-- PANEL: Settings (Moved to Left) -->
             <div class="edit-panel" v-if="media.type != 'category'">
                 <h5 class="panel-title">Settings</h5>
                 
                 <b-row>
                     <b-col md="6">
                         <b-form-group v-if="$helpers.hasShoppingEnabled(media.type)" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2">
                             <span>Shopping</span>
                            <b-form-checkbox id="enable-shopping" v-model="itemShoppingEnabled" switch />
                          </b-form-group>
                          
                          <b-form-group v-if="$helpers.hasRAExclude(media.type)" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2" v-b-popover.hover.left="'Exclude from Recently Added list'">
                             <span>R.A. Exclude</span>
                              <b-form-checkbox id="ra-exclude-checkbox" v-model="itemRAExclude" switch />
                            </b-form-group>
        
                          <b-form-group v-if="$helpers.hasNoRevShare(media.type)" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2" v-b-popover.hover.left="'Revenue share to own account'">
                             <span>Revshare TOA</span>
                            <b-form-checkbox id="no-rev-share-checkbox" v-model="itemNoRevShare" switch />
                          </b-form-group>
                     </b-col>
                     <b-col md="6">
                          <b-form-group v-if="$helpers.hasShowMetaText(media.type)" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2">
                                <span>Show Meta Text</span>
                                <b-form-checkbox id="show-meta-text" v-model="itemShowMetaText" switch />
                          </b-form-group>
                          
                           <b-form-group v-if="$helpers.isPlayableTopLevel(media.type)" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2">
                               <span>Kids Content</span>
                              <b-form-checkbox id="is-kids-content-checkbox" v-model="itemIsKidsContent" switch />
                            </b-form-group>
                            
                            <b-form-group v-if="$helpers.hasCoverEnabled(media.type) && itemShowMetaText" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2">
                              <span>Enable Cover</span>
                              <b-form-checkbox id="enable-cover" v-model="itemCoverEnabled" switch />
                            </b-form-group>
                            
                            <b-form-group v-if="$helpers.hasSlideEnabled(media.type)" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2">
                               <span>Enable Slides</span>
                              <b-form-checkbox id="enable-slide" v-model="itemSlideEnabled" switch />
                            </b-form-group>
                            
                            <b-form-group v-if="$helpers.hasHeaderOverflow(media.type, media.category_type)" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2">
                               <span>Header Overflow</span>
                              <b-form-checkbox id="enable-header-overflow" v-model="itemHeaderOverflowEnabled" switch />
                            </b-form-group>
                            
                            <b-form-group v-if="$helpers.hasQualityStamp(media.type)" class="d-flex justify-content-between border-bottom-soft pb-2 mb-2">
                               <span>Quality Stamp</span>
                              <b-form-checkbox id="quality-stamp-checkbox" v-model="itemQualityStamp" switch />
                            </b-form-group>
                     </b-col>
                 </b-row>
                    
                     <b-form-group v-if="$helpers.hasDetailPage(media.type)" label="Dashboard Position" class="mt-3">
                        <b-form-select id="dashboard-position" v-model="itemEmbeddedDashboardPosition" :options="[{value: 'top', text: 'Top'}, {value: 'bottom', text: 'Bottom'}]" />
                      </b-form-group>
                      
                       <b-form-group v-if="media.type == 'magazine' || media.type == 'series'" :label="`${$helpers.prettify(media.type)} Layout`">
                        <b-form-select id="serial-layout" v-model="itemSerialLayout" :options="serialLayoutOptions" />
                      </b-form-group>
                      
                       <template v-if="$config.enablePpv && (media.type == 'video' || media.type == 'episode')">
                          <b-row>
                              <b-col md="6">
                                  <b-form-group label="PPV" class="mt-2">
                                    <b-form-select id="has-ppv-select" v-model="itemHasPpv" :options="ppvEnabledOptions"/>
                                  </b-form-group>
                              </b-col>
                              <b-col md="6">
                                  <b-form-group label="Price Tier" class="mt-2">
                                    <b-form-select id="price-tier-select" v-model="itemPriceTierId" :options="ppvPriceTierOptions"/>
                                  </b-form-group>
                              </b-col>
                          </b-row>
                          <b-row v-if="media.type == 'video'">
                              <b-col md="12">
                                  <MediaPpvPackages :key="mediaEdit.id" :media-id="mediaEdit.id"/>
                              </b-col>
                          </b-row>
                        </template>

             </div>
            
             <div class="edit-panel" v-if="media.type == 'category' || media.type == 'playlist_page'">
                 <h5 class="panel-title">Category Settings</h5>
                  <template v-if="media.type == 'category' || media.type == 'playlist_page'">
                    <EditDynamicCategory v-if="media.category_type == 'dynamic'" :language="media.default_language" :subject="media.id" :parent="media.parent" :format="(mediaEdit.category_layout || {}).format" :type="media.type" :asset-format="media.asset_format" @update="saveEditCategoryFilter"/>
                    <EditManualCategory v-if="media.category_type == 'manual'" :items="categoryItems" :format="categoryLayoutFormat" :lang="media.default_language" @update="saveEditManualItems" :asset-format="media.asset_format"/>
                    <RecentlyAddedCategoyContent v-if="media.category_type == 'recently-added'" :language="media.default_language" :subject="media.id"/>
                    <ContentTable v-if="media.category_type == 'top-artist'" :items="categoryContent"/>
                    <template v-if="media.category_type == 'rss'">
                      <b-form-group label="News Source" label-for="rss-mode-select" description="Which feeds fill this category. Feeds and their labels are managed on the RSS Feeds page.">
                        <b-form-select id="rss-mode-select" v-model="rssMode" :options="rssModeOptions"/>
                      </b-form-group>
                      <b-form-group v-if="rssMode == 'feed'" label="RSS Feed" label-for="rss-feed-select">
                        <b-form-select id="rss-feed-select" v-model="mediaEdit.rss_feed_id" :options="rssFeedOptions"/>
                      </b-form-group>
                      <b-form-group v-if="rssMode == 'label'" label="Label" label-for="rss-label-select">
                        <b-form-select id="rss-label-select" v-model="mediaEdit.rss_label" :options="rssLabelOptions"/>
                      </b-form-group>
                    </template>
                  </template>
                  
                  <div v-if="(media.type == 'category' || media.type == 'product_category' || media.type == 'playlist_page') && media.category_type != 'root' && media.category_layout != null" class="mt-4">
                     <div class="d-flex justify-content-between align-items-center mb-3">
                         <h6 class="text-muted mb-0">Layout Configuration</h6>
                         <b-form-checkbox id="enable-page" v-model="itemPageEnabled" switch><span class="ml-2">Enable More Page</span></b-form-checkbox>
                      </div>

                      <b-row>
                          <b-col md="6">
                              <b-form-group label="Format">
                                <b-form-select id="category-format-select" v-model="categoryLayoutFormat" :options="categoryLayoutFormatOptions"/>
                              </b-form-group>
                          </b-col>
                          <b-col md="6">
                              <b-form-group label="Formfactor">
                                 <b-form-select id="category-layout-formfactor-select" v-model="categoryLayoutFormfactor" :options="categoryLayoutFormfactorOptions"/>
                              </b-form-group>
                          </b-col>
                           <b-col md="6">
                              <b-form-group label="Size">
                                <b-form-select id="category-layout-size-select" v-model="categoryLayoutSize" :options="categoryLayoutSizeOptions"/>
                              </b-form-group>
                           </b-col>
                      </b-row>
                      
                       <b-row>
                            <b-col md="6">
                                <b-form-checkbox id="category-layout-show-title" v-model="categoryLayoutShowTitles" switch class="mb-2"><span class="ml-2">Show Titles</span></b-form-checkbox>
                                <b-form-checkbox v-if="!disableShowOverlay" id="category-layout-show-overlay" v-model="categoryLayoutShowOverlay" switch><span class="ml-2">Overlay Visible</span></b-form-checkbox>
                            </b-col>
                            <b-col md="6">
                                 <b-form-group v-if="media.type != 'playlist_page'" label="Ad Mode" class="mb-0">
                                    <b-form-select id="category-ad-mode-select" v-model="categoryAdMode" :options="categoryAdModeOptions"/>
                                  </b-form-group>
                            </b-col>
                       </b-row>
                  </div>
             </div>

             <!-- Add Second Save Button at bottom of main column -->
             <div class="d-flex justify-content-end align-items-center mt-3 mb-5">
                <b-button variant="primary" size="lg" @click="$emit('save', {id: mediaId})" class="shadow px-5">
                    <Icon icon="Save" class="mr-2"/> Save Changes
                </b-button>
             </div>

        </div>

        <!-- RIGHT COLUMN (Sidebar) -->
        <div class="grid-sidebar">
        
             <!-- PANEL: Classification -->
             <div class="edit-panel" v-if="media.category_type != 'root'">
                  <h5 class="panel-title">Classification</h5>
                  
                   <b-form-group label="Partner" v-if="media.category_type != 'root'">
                     <b-form-select id="partner-select" v-model="itemPartner" :options="partnerOptions" />
                   </b-form-group>
                   
                   <b-form-group label="Plan" v-if="$helpers.isPlayable(media.type) || $helpers.isTopLevel(media.type)">
                      <b-form-select id="plan-select" v-model="itemPlan" :options="$store.state.media.options.plan"/>
                   </b-form-group>
                   
                   <b-form-group v-if="$config.usePremiumValue && $helpers.hasPremiumValue(media.type, itemPlan) && isPremiumType(media.type)" label="Premium Value">
                      <b-form-select id="premium-value-select" v-model="itemPremiumValue" :options="$store.state.media.options.premiumValue"/>
                   </b-form-group>

                    <template v-if="media.type == 'music_album' || media.type == 'album_track' ||  $config.enableArtistsEverywhere" >
                      <b-form-group label="Artists" label-for="artists-comp">
                        <LabelsInput id="artists-comp" :language="mediaEdit.default_language" :labels="mediaEdit.artists" @update="saveEditArtists" label-type="artist"/>
                      </b-form-group>
                    </template>
    
                    <template v-if="$config.enableGenres && media.type != 'category'" >
                      <b-form-group label="Genres" label-for="genres-comp">
                        <LabelsInput id="genres-comp" :language="mediaEdit.default_language" :labels="mediaEdit.genres" @update="saveEditGenres" label-type="genre"/>
                      </b-form-group>
                    </template>
                    
                    <b-form-group v-if="($helpers.isTopLevel(media.type) || $helpers.isPlayable(media.type)) && media.type != 'photo_package'"  label="Labels" label-for="labels-comp">
                        <LabelsInput id="labels-comp" :language="mediaEdit.default_language" :labels="mediaEdit.labels" @update="saveEditLabels"/>
                    </b-form-group>
             </div>
             
             <!-- PANEL: Publishing -->
             <div class="edit-panel" v-if="media.type != 'category'">
                  <h5 class="panel-title">Publishing</h5>
                  
                   <!-- Refactored Date Picker -->
                   <b-form-group label="Release Date" class="mb-3">
                     <b-form-datepicker id="release-date-picker" v-model="itemReleaseDate" locale="en" class="mb-2" 
                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }"
                        placeholder="Choose a date"
                        menu-class="bg-dark text-light border-secondary"
                     ></b-form-datepicker>
                   </b-form-group>
                   
                   <b-form-group label="Year of Release" class="mb-4">
                     <b-form-input id="year-input" v-model.number="itemYear" :state="yearState" type="number" no-wheel></b-form-input>
                   </b-form-group>

                  <b-form-group label="Schedule">
                    <Publication id="publication-comp" v-model="mediaEdit"/>
                  </b-form-group>
                  
                  <template v-if="$helpers.isTopLevel(media.type)">
                      <b-form-group label="Geo Whitelist" v-b-popover.hover.top="'Only allow access from these locations'">
                        <Geos id="whitelist-comp" title="Geo Whitelist — Allowed Locations" :url="geosUrl" v-model="itemGeoWhitelist"/>
                      </b-form-group>
                      <b-form-group label="Geo Blacklist" v-b-popover.hover.top="'Block access from these locations'">
                        <Geos id="blacklist-comp" title="Geo Blacklist — Blocked Locations" :url="geosUrl" v-model="itemGeoBlacklist"/>
                      </b-form-group>
                  </template>
             </div>
             
             <!-- PANEL: Metadata -->
             <div class="edit-panel">
                 <h5 class="panel-title">Metadata</h5>
                 
                 <b-form-group label="ID">
                    <id-input id="id-input" :value="mediaEdit.id"/>
                 </b-form-group>
                 
                  <template v-if="mediaEdit.duplicate_of != null">
                      <b-form-group label="Duplicate of">
                        <b-input-group>
                        <b-input id="duplicate-of-input" :value="mediaEdit.duplicate_of" readonly></b-input>
                          <b-input-group-append>
                            <b-button v-b-tooltip.hover title="Remove duplicate reference" variant="danger" @click.stop="mediaEdit.duplicate_of = null">
                              <font-awesome-icon icon="trash-alt"/>
                            </b-button>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </template>
                    
                    <b-row class="mb-3">
                        <b-col cols="6">
                             <small class="text-muted d-block">Source</small>
                             <span class="text-break small d-block">{{ mediaEdit.source || 'Unknown' }}</span>
                        </b-col>
                         <b-col cols="6">
                             <small class="text-muted d-block">Fallback Lang</small>
                             <span>{{ defaultLanguageName }}</span>
                        </b-col>
                    </b-row>
                    
                    <template v-if="$config.useExternalIDs">
                      <b-form-group v-if="$helpers.hasUPC(media.type)" label="UPC">
                        <b-form-input id="upc-input" v-model="itemUPC" ></b-form-input>
                      </b-form-group>
                      <b-form-group v-if="$helpers.hasISRC(media.type)" label="ISRC">
                        <b-form-textarea id="isrc-input" v-model="itemISRC" rows="2"></b-form-textarea>
                      </b-form-group>
                       <b-form-group v-if="$helpers.hasISRC(media.type)" label="Reporting Code">
                        <b-form-input id="reporting-code-input" v-model="itemReportingCode" ></b-form-input>
                      </b-form-group>
                    </template>
                    
                     <b-form-group v-if="!(media.type == 'year' || media.type == 'season' || media.type == 'photo_package')" label="Search Tags">
                      <b-form-textarea id="search-tags-textarea" v-model="metaSearchTags" rows="2"></b-form-textarea>
                    </b-form-group>
                    
                    <div class="text-muted small mt-3" style="font-size: 0.75rem;">
                      <div v-if="mediaEdit.inserted_at">Created: {{ formatDate(mediaEdit.inserted_at) }}</div>
                      <div v-if="mediaEdit.updated_at">Updated: {{ formatDate(mediaEdit.updated_at) }}</div>
                    </div>

             </div>
        </div>
      </b-form>

    <SimpleModal :value='deleteMetaModalVisible' @ok="doDeleteMeta" @cancel="deleteMetaModalVisible = false" msg="Are you sure you want to permanently delete all meta data for the selected language?"/>
    <template v-if="media.type == 'category' || media.type == 'page' || media.type == 'artist_page' || media.type == 'playlist_page'">
      <SelectItem v-model="selectPreviewUrlVisible" title="Select Preview Video" :lang="media.default_language" types="video,episode" require-preview @selected="mediaEdit.preview_url = $event.preview_url"/>
    </template>
  </div>
</template>

<script>
import IdInput from "./IdInput.vue"
import Geos from "./Geos.vue"
import LabelsInput from "./LabelsInput.vue"
import ImageList from "./ImageList.vue"
import SimpleModal from "./SimpleModal.vue"
import Publication from "./Publication.vue"
import SubtitleList from "./SubtitleList.vue"
import AssetList from "./AssetList.vue"
import EmbeddedAsset from "./EmbeddedAsset.vue"
import EditDynamicCategory from "./EditDynamicCategory.vue"
import EditManualCategory from "./EditManualCategory.vue"
import ContentTable from "./ContentTable.vue"
import RecentlyAddedCategoyContent from "./RecentlyAddedCategoryContent.vue"
import SimpleAutoComplete from "./SimpleAutoComplete.vue"
import SelectItem from "./SelectItem.vue"
import MediaPpvPackages from "./MediaPpvPackages.vue"
import Credits from "./Credits.vue"

export default {
  name: 'EditMedia',

  components: {
    IdInput,
    LabelsInput,
    Geos,
    ImageList,
    SimpleModal,
    Publication,
    SubtitleList,
    AssetList,
    EmbeddedAsset,
    EditDynamicCategory,
    EditManualCategory,
    ContentTable,
    RecentlyAddedCategoyContent,
    SimpleAutoComplete,
    SelectItem,
    MediaPpvPackages,
    Credits
  },

  props: {
    mediaId: String
  },

  data() {
    return {
      DEFAULT_EDITOR: {
        org: {
          id: this.mediaId,
          default_language: this.$config.defaultLanguage,
          assets: [],
          category_layout: {format: "cover", size: "md", formfactor: "portrait"}
        },
        edit: {
          id: this.mediaId,
          default_language: this.$config.defaultLanguage,
          assets: [],
          category_layout: {format: "cover", size: "md", formfactor: "portrait"}
        },
        currentMeta: {
          language: this.$config.defaultLanguage
        },
        selectedLanguage: this.$config.defaultLanguage
      },

      mediaDefaultLang: "nl",

      slugState: null,
      titleState: null,
      yearState: null,
      c2aUrlState: null,

      saveSuccessCountdown: 0,

      deleteMetaModalVisible: false,

      selectPreviewUrlVisible: false,

      c2aItems: [],
      c2aFilter: null,

      rssFeeds: [],
      rssLabels: []

    }
  },

  computed: {
    // The stored rss_mode, defaulting to a single feed (also covers
    // categories created before the mode existed).
    rssMode: {
      get() {
        return this.mediaEdit.rss_mode || 'feed'
      },
      set(value) {
        this.$set(this.mediaEdit, 'rss_mode', value)
      }
    },

    rssModeOptions() {
      return [
        { value: 'feed', text: 'A specific feed' },
        { value: 'label', text: 'All feeds with a label' },
        { value: 'all', text: 'All feeds' }
      ]
    },

    rssFeedOptions() {
      const options = this.rssFeeds.map(feed => ({
        value: feed.id,
        text: (feed.title || feed.feed_title || feed.feed_url) + (feed.active ? '' : ' (inactive)')
      }))

      return [{ value: null, text: '— no feed selected —' }].concat(options)
    },

    rssLabelOptions() {
      const options = this.rssLabels.map(label => ({ value: label, text: label }))

      return [{ value: null, text: '— no label selected —' }].concat(options)
    },

    editor() {
      return this.$store.state.media.edits[this.mediaId] || this.DEFAULT_EDITOR
    },

    media: {
      get() {
        return this.editor.org
      }
    },

    mediaEdit: {
      get() {
        return this.editor.edit
      },

      set(value) {
        this.$store.commit('media/putEditItem', value)
      }
    },

    currentMeta: {
      get() {
        return this.editor.currentMeta
      },

      set(value) {
        this.$store.commit('media/putCurrentMeta', value)
      }
    },

    selectedLanguage: {
      get() {
        return this.editor.selectedLanguage
      },

      set(value) {
        this.$store.dispatch('media/setLanguageEditor', {lang: value, id: this.mediaId})
      }
    },

    categoryAdModeOptions() {
      return this.$store.state.media.options.categoryAdMode
    },

    planName() {
      return this.$helpers.prettify(this.itemPlan)
    },

    defaultLanguageName() {
      return this.$helpers.getLanguageName(this.mediaEdit.default_language);
    },

    labelsUrl() {
      return "/admin/api/v2/labels";
    },

    genresUrl() {
      return "/admin/api/v2/keywords?language=" + this.mediaEdit.default_language;
    },

    geosUrl() {
      return "/admin/api/v2/media/geo-options"
    },

    assetSource() {
      switch(this.mediaEdit.asset_format) {
        case "external_video": return "External";
        case "external_print": return "External";
        case "external_audio": return "External";
        default: return "Internal"
      }
    },

    hasCovers() {
      const type = this.mediaEdit.type
      const categoryType = this.mediaEdit.category_type
      return type != "season" && type != "year" && type != "photo" && categoryType != 'root' && this.currentMeta.covers
    },

    hasSlides() {
      const type = this.mediaEdit.type
      return type != "season" && type != "year" && type != "photo" && this.currentMeta.slides
    },

    hasThumbnails() {
      const type = this.mediaEdit.type
      return this.$helpers.isPlayable(type)
    },

    hasDashboard() {
      const type = this.mediaEdit.type

      return type != "category" && type != "season" && type != "year"
    },

    hasFormatSelect() {
      const type = this.mediaEdit.category_type

      return (type == 'manual' || type == 'dynamic')
    },

    itemUPC: {
      get() {
        return this.mediaEdit.upc
      },
      set(val) {
        this.updateEditItem('upc', val)
      }
    },
    itemISRC: {
      get() {
        return this.mediaEdit.isrc.join('\r\n')
      },
      set(val) {
        if(val) {
          const values = this.$helpers.linebreakSplit(val).map(s => s.trim())
          this.updateEditItem('isrc', values)
        }
      }
    },

    itemReportingCode: {
      get() {
        return this.mediaEdit.reporting_code
      },
      set(val) {
        this.updateEditItem('reporting_code', val)
      }
    },

    itemPartner: {
      get() {
        return this.mediaEdit.partner_id
      },

      set(val) {
        this.updateEditItem('partner_id', val)
      }
    },

    itemPlan: {
      get() {
        return this.mediaEdit.plan
      },

      set(val) {
        this.updateEditItem('plan', val)
      }
    },

    itemPremiumValue: {
      get() {
        return this.mediaEdit.premium_value
      },

      set(val) {
        this.updateEditItem('premium_value', parseInt(val))
      }
    },

    itemHasPpv: {
      get() {
        return this.mediaEdit.has_ppv
      },

      set(val) {
        this.updateEditItem('has_ppv', val)
      }
    },

    itemPriceTierId: {
      get() {
        return this.mediaEdit.price_tier_id
      },

      set(val) {
        this.updateEditItem('price_tier_id', val)
      }
    },

    ppvEnabledOptions() {
      return [
        { value: null, text: 'Default' },
        { value: true, text: 'Enabled' },
        { value: false, text: 'Disabled' }
      ]
    },

    ppvPriceTierOptions() {
      const tiers = this.$store.state.media.options.ppvPriceTiers || []
      const options = [{ value: null, text: 'Default' }]
      tiers.forEach(tier => {
        if (tier.is_active) {
          options.push({ value: tier.id, text: tier.name })
        }
      })
      return options
    },

    itemIsLivestream: {
      get() {
        return this.mediaEdit.is_livestream
      },

      set(val) {
        this.updateEditItem('is_livestream', val)
      }
    },

    itemLivestreamFinished: {
      get() {
        return this.mediaEdit.livestream_finished
      },

      set(val) {
        this.updateEditItem('livestream_finished', val)
      }
    },

    itemLivestreamFinishedUrl: {
      get() {
        return this.mediaEdit.livestream_finished_url
      },

      set(val) {
        this.updateEditItem('livestream_finished_url', val)
      }
    },

    itemLivestreamFinishedUrlType: {
      get() {
        return this.mediaEdit.livestream_finished_url_type || 'm3u8'
      },

      set(val) {
        this.updateEditItem('livestream_finished_url_type', val)
      }
    },

    itemLivestreamTimezone: {
      get() {
        return this.mediaEdit.livestream_timezone
      },

      set(val) {
        this.updateEditItem('livestream_timezone', val)
      }
    },

    itemLivestreamFinishedStartOffsetMinutes: {
      get() {
        return Math.floor((this.mediaEdit.livestream_finished_start_offset || 0) / 60)
      },

      set(val) {
        let mins = parseInt(val, 10) || 0
        let secs = (this.mediaEdit.livestream_finished_start_offset || 0) % 60
        this.setLivestreamFinishedStartOffset(mins * 60 + secs)
      }
    },

    itemLivestreamFinishedStartOffsetSeconds: {
      get() {
        return (this.mediaEdit.livestream_finished_start_offset || 0) % 60
      },

      set(val) {
        let secs = parseInt(val, 10) || 0
        let mins = Math.floor((this.mediaEdit.livestream_finished_start_offset || 0) / 60)
        this.setLivestreamFinishedStartOffset(mins * 60 + secs)
      }
    },

    finishedUrlTypeOptions() {
      return [
        { value: 'm3u8', text: 'm3u8' },
        { value: 'cloudflare', text: 'Cloudflare' }
      ]
    },

    timezoneOptions() {
      let zones = []
      try {
        if (typeof Intl.supportedValuesOf === 'function') {
          zones = Intl.supportedValuesOf('timeZone')
        }
      } catch (e) {
        zones = []
      }

      if (!zones.length) {
        zones = ['UTC', 'Europe/Amsterdam', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo']
      }

      return [{ value: null, text: 'Local (browser)' }].concat(zones.map(z => ({ value: z, text: z })))
    },

    finishedUrlState() {
      const url = this.mediaEdit.livestream_finished_url
      if (!url) {
        return null
      }

      if (this.itemLivestreamFinishedUrlType == 'm3u8') {
        return url.startsWith('http') && url.endsWith('m3u8')
      }

      return url.startsWith('http')
    },

    itemRAExclude: {
      get() {
        return this.mediaEdit.recently_added_exclude
      },

      set(val) {
        this.updateEditItem('recently_added_exclude', val)
      }
    },

    itemSerialLayout: {
      get() {
        return this.mediaEdit.serial_layout
      },

      set(val) {
        this.updateEditItem('serial_layout', val)
      }
    },

    itemEmbeddedDashboardPosition: {
      get() {
        return this.mediaEdit.embedded_dashboard_position
      },

      set(val) {
        this.updateEditItem('embedded_dashboard_position', val)
      }
    },

    itemYear: {
      get() {
        return this.mediaEdit.year
      },

      set(val) {
        this.updateEditItem('year', val)
        if(val == "" || (val >= 1800 && val <= 2100)) this.yearState = true
        else this.yearState = false
      }
    },

    itemGenre: {
      get() {
        return this.mediaEdit.genre
      },

      set(val) {
        this.updateEditItem('genre', val)
      }
    },

    itemReleaseDate: {
      get() {
        return this.mediaEdit.release_date
      },

      set(val) {
        this.updateEditItem('release_date', val)
      }
    },


    itemNoRevShare: {
      get() {
        return this.mediaEdit.no_rev_share
      },

      set(val) {
        this.updateEditItem('no_rev_share', val)
      }
    },

    itemShowMetaText: {
      get() {
        return this.mediaEdit.show_meta_text
      },

      set(val) {
        this.updateEditItem('show_meta_text', val)
      }
    },

    itemCoverEnabled: {
      get() {
        return this.mediaEdit.cover_enabled
      },

      set(val) {
        this.updateEditItem('cover_enabled', val)
      }
    },

    itemPageEnabled: {
      get() {
        return this.mediaEdit.page_enabled
      },

      set(val) {
        this.updateEditItem('page_enabled', val)
      }
    },

    itemSlideEnabled: {
      get() {
        return this.mediaEdit.slide_enabled
      },

      set(val) {
        this.updateEditItem('slide_enabled', val)
      }
    },

    itemHeaderOverflowEnabled: {
      get() {
        return this.mediaEdit.header_overflow
      },

      set(val) {
        this.updateEditItem('header_overflow', val)
      }
    },

    itemIsKidsContent: {
      get() {
        return this.mediaEdit.is_kids_content
      },

      set(val) {
        this.updateEditItem('is_kids_content', val)
      }
    },

    itemQualityStamp: {
      get() {
        return this.mediaEdit.quality_stamp
      },

      set(val) {
        this.updateEditItem('quality_stamp', val)
      }
    },

    itemShoppingEnabled: {
      get() {
        return this.mediaEdit.shopping_enabled
      },

      set(val) {
        this.updateEditItem('shopping_enabled', val)
      }
    },

    itemGeoWhitelist: {
      get() {
        return this.mediaEdit.geo_whitelist
      },

      set(val) {
        this.updateEditItem('geo_whitelist', val)
      }
    },

    itemGeoBlacklist: {
      get() {
        return this.mediaEdit.geo_blacklist
      },

      set(val) {
        this.updateEditItem('geo_blacklist', val)
      }
    },

    itemCredits: {
      get() {
        return this.mediaEdit.credits
      },

      set(val) {
        this.updateEditItem('credits', val)
      }
    },

    categoryItems: {
      get() {
        return this.mediaEdit.category_items
      },

      set(value) {
        this.updateEditItem('category_items', value)
      }
    },

    categoryContent: {
      get() {
        return this.mediaEdit.category_content
      },

      set(value) {
        this.updateEditItem('category_content', value)
      }
    },

    categoryAdMode: {
      get() {
        return this.mediaEdit.category_ad_mode
      },

      set(value) {
        this.updateEditItem('category_ad_mode', value)
      }
    },

    categoryLayoutFormat: {
      get() {
        return (this.mediaEdit.category_layout || {}).format
      },

      set(value) {
        let cl = this.mediaEdit.category_layout || {}
        cl.format = value
        this.updateEditItem('category_layout', cl)
      }
    },

    categoryLayoutFormfactor: {
      get() {
        return (this.mediaEdit.category_layout || {}).formfactor
      },

      set(value) {
        let cl = this.mediaEdit.category_layout || {}
        cl.formfactor = value
        this.updateEditItem('category_layout', cl)
      }
    },

    categoryLayoutSize: {
      get() {
        return (this.mediaEdit.category_layout || {}).size
      },

      set(value) {
        let cl = this.mediaEdit.category_layout || {}
        cl.size = value
        this.updateEditItem('category_layout', cl)
      }
    },

    categoryLayoutShowTitles: {
      get() {
        return (this.mediaEdit.category_layout || {}).show_titles
      },

      set(value) {
        let cl = this.mediaEdit.category_layout || {}
        cl.show_titles= value
        this.updateEditItem('category_layout', cl)
      }
    },

    categoryLayoutShowOverlay: {
      get() {
        return (this.mediaEdit.category_layout || {}).show_overlay
      },

      set(value) {
        let cl = this.mediaEdit.category_layout || {}
        cl.show_overlay = value
        this.updateEditItem('category_layout', cl)
      }
    },

    metaTitle: {
      get() {
        return this.currentMeta.title
      },
      set(val) {
        this.updateEditMeta('title', val)
        this.titleState = !!val
      }
    },

    metaSecTitle: {
      get() {
        return this.currentMeta.secondary_title
      },
      set(val) {
        this.updateEditMeta('secondary_title', val)
      }
    },

    metaSlug: {
      get() {
        return this.currentMeta.slug
      },

      set(val) {
        this.updateEditMeta('slug', val)

        const slug = {
          language: this.currentMeta.language,
          type: this.mediaEdit.type,
          slug: val
        }

        this.$http.postJSON(
          `/admin/api/v2/slugs/${this.mediaEdit.id}/check`,
          {data: slug},
          () => {
            this.slugState = true;
          },
          (status, html) => {
            if(status == 400) {
              this.slugState = false;
            }
            else {
              this.slugState = false;
              this.$root.app.showErrorDialog(html, status.toString())
            }
          }
        )
      }
    },

    metaShortDescription: {
      get() {
        return this.currentMeta.short_description
      },

      set(val) {
        this.updateEditMeta('short_description', val)
      }
    },

    metaLongDescription: {
      get() {
        return this.currentMeta.long_description
      },

      set(val) {
        this.updateEditMeta('long_description', val)
      },
    },

    metaCallToAction: {
      get() {
        return this.currentMeta.call_to_action_text
      },

      set(val) {
        this.updateEditMeta('call_to_action_text', val)
      }
    },

    metaCallToActionURL: {
      get() {
        return this.currentMeta.call_to_action_url
      },

      set(val) {
        this.updateEditMeta('call_to_action_url', val)
        if(val && val.startsWith('https://')) { this.c2aUrlState = true }
        else if(val && !val.startsWith('https://')) { this.c2aUrlState = false }
        else { this.c2aUrlState = null }
      }
    },

    metaSocialMediaDescription: {
      get() {
        return this.currentMeta.sm_description
      },

      set(val) {
        this.updateEditMeta('sm_description', val)
      }
    },

    metaSearchTags: {
      get() {
        return this.currentMeta.search_tags
      },

      set(val) {
        this.updateEditMeta('search_tags', val)
      }
    },

    metaLinkTo: {
      get() {
        return this.currentMeta.link_to
      },

      set(val) {
        this.updateEditMeta('link_to', val)
      }
    },

    metaCovers: {
      get() {
        return this.currentMeta.covers
      },

      set(val) {
        this.updateEditMeta('covers', val)
      }
    },

    metaSlides: {
      get() {
        return this.currentMeta.slides
      },

      set(val) {
        this.updateEditMeta('slides', val)
      }
    },

    langOptions() {
      return this.$store.state.media.options.language
    },

    partnerOptions() {
      return this.$store.state.media.options.partner
    },

    serialLayoutOptions() {
      return this.$store.state.media.options.serialLayout
    },

    categoryLayoutFormatOptions() {
      return this.$store.state.media.options.format
    },

    categoryLayoutFormfactorOptions() {
      return this.$store.state.media.options.formfactor
    },

    categoryLayoutSizeOptions() {
      return this.$store.state.media.options.formatSize
    },

    disableShowOverlay() {
      const cf = this.mediaEdit.category_filter

      return !(cf && cf.type.indexOf('photo') != -1)
    }

  },

  methods: {
    isPremiumType(mediaType) {
      return this.$_.includes(this.$store.state.media.premiumTypes, mediaType)
    },

    updateEditItem(field, value) {
      let update = {}
      update[this.mediaId] = {}
      update[this.mediaId][field] = value
      this.$store.commit('media/updateEditItem', update)
    },

    setLivestreamFinishedStartOffset(total) {
      this.updateEditItem('livestream_finished_start_offset', total > 0 ? total : null)
    },

    updateEditMeta(field, value) {
      let update = {}
      update[this.mediaId] = {}
      update[this.mediaId][field] = value
      this.$store.commit('media/updateEditMeta', update)
    },

    getCallToActions(lang) {
      fetch(`/admin/api/v2/media/all-call-to-actions?lang=${lang}`)
      .then(this.$helpers.JSONHandler)
      .then(resp => {
        this.c2aItems = resp.data
      });
    },

    setCurrentMeta(lang) {
      this.getCallToActions(lang)

      const allMeta = this.mediaEdit.meta || []
      let newMeta = allMeta.filter(m => m.language == lang)

      if(newMeta.length == 0) newMeta[0] = {
        language: lang
      }

      this.$store.commit('media/putCurrentMeta', newMeta[0])
    },

    deleteMeta() {
      this.deleteMetaModalVisible = true
    },

    doDeleteMeta() {
      this.$store.dispatch('media/deleteEditMeta', {lang: this.selectedLanguage, id: this.mediaId})
      .then(() => {
        this.$root.app.showAlert("Language metadata successfully deleted", "success")
      })
      .catch(({text}) => {
         this.$root.app.showAlert(`Error deleting language metadata: ${text}`, 'danger')
      })
    },

    clear() {
      this.closeModalVisible = false
      this.slugState = null
      this.titleState = null
    },

    makeSlugFromTitle() {
      const slug = {
        language: this.currentMeta.language,
        type: this.mediaEdit.type,
        slug: this.metaTitle
      }
      this.$http.postJSON(
        `/admin/api/v2/slugs/${this.mediaEdit.id}/make`,
        {data: slug},
        (resp) => {
          this.metaSlug = resp.data
          this.slugState = true
        },
        (status, html) => this.$root.app.showErrorDialog(html, status.toString())
      )
    },

    makeSecTitleFromArtists() {
      this.metaSecTitle = (this.mediaEdit.artists || []).join(', ')
    },

    // TODO
    copyCoversFrom(lang) {
      const allMeta = this.mediaEdit.meta
      let images = []

      for(var i = 0; i < allMeta.length; ++i) {
        if(allMeta[i].language == lang) {
          images = this.$_.cloneDeep(allMeta[i].covers)
          break
        }
      }
      this.$log.debug(images)
      if(images.length > 0) {
        this.$http.postJSON(
          `admin/api/v2/media/${this.mediaId}/add-covers`,
          {data: {covers: images, language: this.currentMeta.language}},
          () => {
            this.$set(this.currentMeta, "covers", images)
          },
          (status, html) => this.$root.app.showErrorDialog(html, status.toString())
        )
      }
    },

    // TODO
    deleteAsset(id) {
      let newAssets = this.mediaEdit.assets.filter(asset => asset.id != id)
      this.$set(this.mediaEdit, "assets", newAssets)
    },

    // TODO
    retranscodeAsset(id) {
      let newAssets = this.mediaEdit.assets.map(asset => {
        if(asset.id == id) {
          asset.status = "retranscoding"
        }

        return asset;
      })
      this.$set(this.mediaEdit, "assets", newAssets)
    },

    saveEditLabels(labels) {
      this.updateEditItem('labels', labels)
    },

    saveEditArtists(artists) {
      this.updateEditItem('artists', artists)
    },

    saveEditGenres(genres) {
      this.updateEditItem('genres', genres)
    },

    saveEditCategoryFilter(filter) {
      this.mediaEdit.category_filter = filter
    },

    loadRssFeeds() {
      fetch('/admin/api/v2/rss-feeds')
        .then(this.$helpers.JSONHandler)
        .then(resp => {
          this.rssFeeds = resp.data || []
        })
        .catch(() => {
          this.$root.app.showAlert('Error loading RSS feeds', 'danger')
        })

      fetch('/admin/api/v2/rss-feeds/labels')
        .then(this.$helpers.JSONHandler)
        .then(resp => {
          this.rssLabels = resp.data || []
        })
        .catch(() => {
          // Labels are optional; the label picker will just be empty
        })
    },

    saveEditManualItems(items) {
      this.categoryItems = items.map((item, ndx) => {
        return {id: item.id, position: ndx + 1}
      })
    },

    saveEditWhitelist(whitelist) {
      this.updateEditItem('geo_whitelist', whitelist)
    },

    saveEditBlacklist(blacklist) {
      this.updateEditItem('geo_blacklist', blacklist)
    },

    tryReload(delay) {
      setTimeout(function() {
        this.$store.dispatch('media/fetchEditItem', this.mediaEdit.id)
        .then(json => {
          this.$store.commit('media/putEditItem', json.data)
        })
      }.bind(this), delay)
    },

    selectPreviewUrl() {
      this.selectPreviewUrlVisible = true
    },

    formatDate(isoString) {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  },

  mounted() {
    // Refresh PPV price tiers when component loads (in case they were edited on PPV page)
    if (this.$config.enablePpv && (this.media.type === 'video' || this.media.type === 'episode')) {
      this.$store.dispatch('media/fetchPpvPriceTiers')
    }

    // Feeds for the RSS category feed picker
    if (this.$config.enableRssFeeds && (this.media.type === 'category' || this.media.type === 'playlist_page')) {
      this.loadRssFeeds()
    }
  }

}
</script>


<style scoped lang="scss">
// Container for the whole edit view
.edit-media-container {
  padding: 0 15px; // Outer padding
}

.edit-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 1rem;
}

// Grid Layout System
.edit-form-grid {
  display: grid;
  grid-template-columns: 1fr; // Mobile default
  gap: 24px;

  @media (min-width: 992px) { // lg breakpoint
    grid-template-columns: 2fr 1fr; // 2/3 and 1/3 split
  }

  .grid-main {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .grid-sidebar {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
}

// Panel Styling (Glassmorphism Cards)
// .edit-panel is now handled by .glass-panel global class if applied, 
// OR we keep this specific class but map it to the global mixin if we had one.
// Since we want to use the global styles, we should ideally use the '.glass-panel' class in the template 
// or let .edit-panel inherit from it. 
// For now, I will rename the class in the template or leave it as a specific override if needed.
// However, since we defined .glass-panel globally, let's keep valid specific overrides here if any.
// Actually, to fully migrate, I should update the template classes or map .edit-panel to the same styles.
// Better approach: Rely on the global styles. But I need to make sure .edit-panel matches .glass-panel.
// I will keep .edit-panel here but reduce it to just layout specifics if mostly handled globally.
// BUT, since we just appended to custom.scss, we didn't change the template classes yet. 
// So I will make .edit-panel match the global style by referencing the same properties 
// or simply assume I will update the template to use .glass-panel. 
// To be safe and less intrusive to the template, I will leave .edit-panel here 
// but simply point out it's redundant if I don't change the template.
// Wait, the plan said "Remove scoped SCSS...". 
// I'll keep the grid layout styles.
// I'll remove the input/button overrides.

.edit-panel {
  padding: 24px;
  
  .panel-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: rgba(255, 255, 255, 0.9);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    padding-bottom: 0.75rem;
    letter-spacing: 0.5px;
  }
}

// Action Buttons specific to header layout
.edit-actions {
  .btn {
    padding: 6px 16px; // Slightly smaller in header
  }
}

// Category sections specific
.bg-category {
    background: rgba(255, 255, 255, 0.03) !important;
    border: 1px dashed rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 20px;
    margin-top: 1rem;
    
    h5 {
        color: rgba(255,255,255,0.8);
        border-bottom: none;
    }
}

// ID and timestamps
.text-muted.small {
  font-family: monospace;
  opacity: 0.6;
}

// Calendar overrides are now global
</style>

